import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { CurrencyCode, currencyCodes } from 'gain-lib/money/src/currency';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { groupBy, orderBy, uniqBy } from 'lodash';

interface CountryDataCacheEntry {
  timestamp: string;
  countries: ApiClient.ICountryDto[];
}

const localStorageKey = 'CountryRegionData';

// Sanctioned countries excluded from selection on Overrides pages.
export const SANCTIONED_COUNTRY_CODES = ['RU', 'BY', 'CU', 'IR', 'KP', 'SY'];

function getFromLocalStorage(): CountryDataCacheEntry | null {
  const dataStr: string | null = window.localStorage.getItem(localStorageKey);
  if (dataStr == null) {
    return null;
  }
  return JSON.parse(dataStr) as CountryDataCacheEntry;
}

const expirationPeriodInMs = 60 * 60 * 1000;

function writeToLocalStorage(countries: ApiClient.ICountryDto[]) {
  const entry: CountryDataCacheEntry = {
    timestamp: new Date().toISOString(),
    countries,
  };
  const entryJson = JSON.stringify(entry, null, 2);
  window.localStorage.setItem(localStorageKey, entryJson);
}

@Injectable({ providedIn: 'root' })
export class CountryRegionDataService implements Resolve<void> {
  private _countries: ApiClient.ICountryDto[] = [];
  private _countriesSet: Set<string> = new Set<string>();
  private _countriesWithRegions: ApiClient.ICountryDto[] = [];
  private _regionsSet: Set<string> = new Set<string>();

  public get countries() {
    if (!this._initialized) {
      throw new Error(
        'CountryRegionDataService: Country data not initialized.',
      );
    }
    return this._countries;
  }

  public get countriesWithRegions() {
    if (!this._initialized) {
      throw new Error(
        'CountryRegionDataService: Country data not initialized.',
      );
    }
    return this._countriesWithRegions;
  }

  public get currencyCodes() {
    return this.currencies.map((c) => c.code);
  }

  public get currencies() {
    const currencies = this.countries
      .filter((c) => c.currency != null)
      .map((c) => c.currency!);
    return uniqBy(
      orderBy(currencies, (c) => c.code),
      (c) => c.code,
    );
  }

  // Currency codes to exclude wherever a sanctioned country is itself excluded: a code is dropped only
  // if every country using it is sanctioned (server-side ICountryDto.isSanctioned), so a currency shared
  // with a non-sanctioned country survives.
  public get sanctionedOnlyCurrencyCodes(): string[] {
    const countriesWithCurrency = this.countries.filter(
      (c) => c.currency != null,
    );
    const countriesByCurrencyCode = groupBy(
      countriesWithCurrency,
      (c) => c.currency!.code,
    );
    return Object.entries(countriesByCurrencyCode)
      .filter(([, countriesForCode]) =>
        countriesForCode.every((c) => c.isSanctioned),
      )
      .map(([code]) => code);
  }

  // Single shared source for every currency dropdown that must not offer a sanctioned country's
  // currency — the Overrides amount dialogs and the Income for banding Income/Social pages, which are
  // excluded fields and already hide sanctioned countries themselves (AB#7817589). Avoids re-deriving
  // the filter per caller.
  public get currencyCodeOptions(): CurrencyCode[] {
    const sanctionedOnlyCurrencyCodes = this.sanctionedOnlyCurrencyCodes;
    return currencyCodes.filter(
      (code) => !sanctionedOnlyCurrencyCodes.includes(code),
    );
  }

  private _initialized = false;

  constructor(private _api: ApiClient.CountryRegionDataBffService) {}

  findCountryByCode(code: string): ApiClient.ICountryDto | undefined {
    if (!code) {
      return undefined;
    }
    return this._countries.find(
      (c) => c.code.toLowerCase() === code.toLowerCase(),
    );
  }

  getAllCountriesByNameOrCode(query: string) {
    const queryLower = query.toLowerCase();
    return this._countries.filter(
      (c) =>
        c.code.toLowerCase().includes(queryLower) ||
        c.name.toLowerCase().includes(queryLower),
    );
  }

  getCountryCodeByCountryName(countryName: string): string | null {
    const country = this._countries.find(
      (c) => c.name.toLowerCase() === countryName.toLowerCase(),
    );
    return country ? country.code : null;
  }

  // Country-independent "is this a generic region code" check, mirroring the grouped region
  // dropdown's own source (countriesWithRegions). The region selected when Resident Country = XX
  // comes from any country group shown in the dropdown, so identification must scan across
  // countries rather than under one country. Keyed on isGeneric, never the literal "XX".
  // Known ambiguity (US 7571606): a code can be shared by a generic region (IT "XX") and a real
  // region (US "XX"); a code-only scan treats the shared code as generic, erring toward firing
  // the XX/XX rule, which matches the ticket intent.
  isGenericRegionCode(regionCode: string | null): boolean {
    if (!regionCode) {
      return false;
    }
    const regionCodeLower = regionCode.toLowerCase();
    return this._countriesWithRegions
      .flatMap((c) => c.regions)
      .some((r) => r.code.toLowerCase() === regionCodeLower && r.isGeneric);
  }

  // Country-scoped genericness check for callers that already know the country, e.g. a
  // per-country screen whose own region DTOs carry no isGeneric flag (US 7774335). Prefer
  // this over isGenericRegionCode where the country is known: it avoids that method's
  // documented ambiguity when one code is generic for one country and real for another.
  isGenericRegionForCountry(
    countryCode: string | null,
    regionCode: string | null,
  ): boolean {
    if (!countryCode || !regionCode) {
      return false;
    }
    const regionCodeLower = regionCode.toLowerCase();
    return (
      this.findCountryByCode(countryCode)?.regions.some(
        (r) => r.code.toLowerCase() === regionCodeLower && r.isGeneric,
      ) === true
    );
  }

  getRegionCodeByRegionName(regionName: string): string | null {
    const region = this._countriesWithRegions
      .flatMap((c) => c.regions)
      .find((r) => r.name.toLowerCase() === regionName.toLowerCase());
    return region ? region.code : null;
  }

  findCountryCodeByRegionCode(
    regionCode: string | null,
    preferredCountryCode?: string | null,
  ): string | null {
    if (!regionCode) {
      return null;
    }
    const regionCodeLower = regionCode.toLowerCase();
    const containsRegion = (country: ApiClient.ICountryDto) =>
      country.regions.some((r) => r.code.toLowerCase() === regionCodeLower);

    const preferredCountry =
      preferredCountryCode != null
        ? this.findCountryByCode(preferredCountryCode)
        : undefined;
    if (preferredCountry != null && containsRegion(preferredCountry)) {
      return preferredCountry.code;
    }

    return this._countriesWithRegions.find(containsRegion)?.code ?? null;
  }

  findCountryByCurrencyCode(
    currencyCode: string,
  ): ApiClient.ICountryDto | undefined {
    return this._countries.find((c) => c.currency?.code === currencyCode);
  }

  findCurrencyByCountryCode(
    countryCode: string,
  ): ApiClient.ICurrencyDto | null {
    return (
      this._countries.find((c) => c.code === countryCode)?.currency || null
    );
  }

  getSupportedRegionOptions({
    countryCode,
    regionQuery,
  }: {
    countryCode: string;
    regionQuery?: string | null;
  }) {
    const country = this.findCountryByCode(countryCode);
    const regions =
      country != null && country.supportsRegionCalculations
        ? country.regions
        : [];
    const regionQueryLower = regionQuery?.toLowerCase();
    return regionQueryLower != null
      ? regions.filter(
          (r) =>
            r.name.toLowerCase().includes(regionQueryLower) ||
            r.code.toLowerCase().includes(regionQueryLower),
        )
      : regions;
  }

  getSupportedRegionsByCountryCode(
    code: string[] | string,
  ): ApiClient.IRegionDto[] | undefined {
    // If code is a string, make it an array first
    code = Array.isArray(code) ? code : [code];

    const regions = code
      // Make an array of regions for all country codes provided
      .reduce(
        (r, c) => r.concat(this.findCountryByCode(c) || []),
        [] as ApiClient.ICountryDto[],
      )
      // Filter out only countries that have regions
      .filter((c) => c.supportsRegionCalculations)
      // Flatten the array
      .flatMap((c) => c.regions.sort(this._sortFn));

    return regions.length ? regions : undefined;
  }

  // Regions a user may newly select, with generic/placeholder regions (e.g. XX) removed.
  // Selection sources across Configuration and Overrides read this; display and lookup
  // paths keep using getSupportedRegionsByCountryCode so values already saved against a
  // generic region still resolve to a name (US 7774335 AC7). Keyed on the isGeneric flag,
  // never the literal "XX". Mirrors getSupportedRegionsByCountryCode's contract, including
  // returning undefined rather than an empty array.
  getSelectableRegionsByCountryCode(
    code: string[] | string,
  ): ApiClient.IRegionDto[] | undefined {
    const regions = (this.getSupportedRegionsByCountryCode(code) ?? []).filter(
      (region) => !region.isGeneric,
    );
    return regions.length ? regions : undefined;
  }

  // countriesWithRegions for country-grouped region pickers: each country keeps only its
  // selectable regions, and a country left with none is dropped so the picker renders no
  // empty option group (US 7774335). Returns fresh objects — the cached reference data is
  // never mutated, so other consumers still see the full region lists.
  public get countriesWithSelectableRegions(): ApiClient.ICountryDto[] {
    return this.countriesWithRegions
      .map((country) => ({
        ...country,
        regions: country.regions.filter((region) => !region.isGeneric),
      }))
      .filter((country) => country.regions.length > 0);
  }

  // excludeGeneric: opt-in per caller. Removes generic/placeholder regions (e.g. XX)
  // without changing the shared country/region data (US 7571606).
  getSupportedRegionsAndCountryByCountryCode(
    code: string[] | string,
    opts: { excludeGeneric?: boolean } = {},
  ): { countryCode: string; region: ApiClient.IRegionDto }[] | undefined {
    // If code is a string, make it an array first
    code = Array.isArray(code) ? code : [code];

    const regions = code
      // Make an array of regions for all country codes provided
      .reduce(
        (r, c) => r.concat(this.findCountryByCode(c) || []),
        [] as ApiClient.ICountryDto[],
      )
      // Filter out only countries that have regions
      .filter((c) => c.supportsRegionCalculations)
      // Flatten the array
      .flatMap((c) => {
        return c.regions
          .sort(this._sortFn)
          .filter((region) => !(opts.excludeGeneric && region.isGeneric))
          .map((region) => {
            return {
              countryCode: c.code,
              region: region,
            };
          });
      });

    return regions.length ? regions : undefined;
  }

  async resolve(): Promise<void> {
    return this._initialize();
  }

  private async _initialize(): Promise<void> {
    let cached = getFromLocalStorage();

    if (
      cached == null ||
      new Date().getTime() - new Date(cached.timestamp).getTime() >
        expirationPeriodInMs
    ) {
      if (cached == null) {
        // eslint-disable-next-line no-console
        console.debug('no cached country data, fetching');
      } else {
        // eslint-disable-next-line no-console
        console.debug('country data in cache expired, re-fetching');
      }

      const result = await this._api.getCountryRegionData().toPromise();

      const countries = result.countries
        .map((c) => ({
          ...c,
          regions: c.regions.sort(this._sortFn),
        }))
        .sort(this._sortFn);
      writeToLocalStorage(countries);
    } else {
      // eslint-disable-next-line no-console
      console.debug('restored country data from cache');
    }

    cached = getFromLocalStorage();

    if (cached != null) {
      const countries = cached.countries
        .map((c) => ({
          ...c,
          regions: c.regions.sort(this._sortFn),
        }))
        .sort(this._sortFn);

      this._countries = countries;
      this._countriesSet = new Set(this._countries.map((c) => c.code));
      this._countriesWithRegions = countries.filter(
        (c) => c.supportsRegionCalculations,
      );
      this._regionsSet = new Set(
        this._countriesWithRegions.flatMap((c) =>
          c.regions.map((r) => `${c.code}-${r.code}`),
        ),
      );
    }

    this._initialized = true;
  }

  public isValidCountry(countryCode: string): boolean {
    return this._countriesSet.has(countryCode);
  }

  public isValidRegion(countryCode: string, regionCode: string): boolean {
    return this._regionsSet.has(`${countryCode}-${regionCode}`);
  }

  private _sortFn(
    a: ApiClient.ICountryDto | ApiClient.IRegionDto,
    b: ApiClient.ICountryDto | ApiClient.IRegionDto,
  ): number {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  }
}
